import { useEffect, useMemo, useRef, useState } from 'react'

import { DayPicker, type Matcher } from 'react-day-picker'

import { cn } from '../../../utils/cn'
import { Button } from '../../atoms/button/button'
import { Icon } from '../../atoms/icon/icon'
import { Input } from '../../molecules/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../molecules/select'

/* ---------------------------------- types --------------------------------- */

export interface DateRange {
  from: Date
  to: Date
}

interface DatePickerBaseProps {
  minDate?: Date
  maxDate?: Date
  disabledDays?: (date: Date) => boolean
  confirmLabel?: string
  cancelLabel?: string
  onCancel?: () => void
  onMonthChange?: (month: Date) => void
  className?: string
}

export interface SingleDatePickerProps extends DatePickerBaseProps {
  mode?: 'single'
  value?: Date | null
  defaultValue?: Date | null
  onConfirm?: (date: Date) => void
}

export interface RangeDatePickerProps extends DatePickerBaseProps {
  mode: 'range'
  value?: DateRange | null
  defaultValue?: DateRange | null
  onConfirm?: (range: DateRange) => void
}

export type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps

interface DraftRange {
  from?: Date
  to?: Date
}

/* --------------------------------- helpers -------------------------------- */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

const pad = (value: number) => String(value).padStart(2, '0')

export const formatDate = (date: Date) =>
  `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`

export const parseDate = (text: string): Date | null => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim())
  if (!match) return null
  const [, month, day, year] = match.map(Number)
  const date = new Date(year, month - 1, day)
  const isValid =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  return isValid ? date : null
}

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Masks free text into MM/DD/YYYY as the user types: strips non-digits,
 * clamps month/day segments, and auto-inserts slashes. When the user is
 * deleting, the trailing slash is not re-appended so backspace works.
 */
export const maskDateText = (raw: string, previous = ''): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  const isDeleting = raw.length < previous.length

  let month = digits.slice(0, 2)
  if (month.length === 1 && Number(month) > 1) month = `0${month}`
  if (month.length === 2) {
    if (month === '00') month = '01'
    else if (Number(month) > 12) month = '12'
  }

  let day = digits.slice(2, 4)
  if (day.length === 1 && Number(day) > 3) day = `0${day}`
  if (day.length === 2) {
    if (day === '00') day = '01'
    else if (Number(day) > 31) day = '31'
  }

  const year = digits.slice(4, 8)

  let masked = month
  if (month.length === 2 && (day || !isDeleting)) masked += '/'
  masked += day
  if (day.length === 2 && (year || !isDeleting)) masked += '/'
  masked += year
  return masked
}

/* ------------------------------- day styling ------------------------------ */

const dayPickerClassNames = {
  months: 'relative flex flex-col',
  month: 'flex w-full flex-col gap-4',
  nav: 'hidden',
  month_caption: 'hidden',
  month_grid: 'w-full border-separate [border-spacing:0_8px]',
  weekdays: '',
  weekday:
    'text-surface-body text-caption-lg h-[26px] w-[38px] pr-2 text-center font-semibold last:w-[30px] last:pr-0',
  week: '',
  day: 'h-[26px] w-[38px] py-0 pl-0 pr-2 text-center last:w-[30px] last:pr-0',
  day_button:
    'text-caption-lg text-surface-title-50 hover:bg-surface-200 flex h-[26px] w-[30px] cursor-pointer items-center justify-center rounded font-semibold transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent',
  range_start:
    'bg-background rounded-l [&>button]:bg-primary [&>button]:text-on-primary [&>button]:hover:bg-primary-400',
  range_end:
    'bg-background rounded-r [&>button]:bg-primary [&>button]:text-on-primary [&>button]:hover:bg-primary-400',
  range_middle: 'bg-background [&>button]:hover:bg-surface-200',
  outside: '[&>button]:text-surface-300',
  disabled:
    '[&>button]:text-surface-300 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent',
  hidden: 'invisible'
}

/* -------------------------------- component ------------------------------- */

const fromToInputProps = {
  size: 'sm' as const,
  leadingIcon: <Icon name='calendar_today' className='text-[18px]' />,
  placeholder: 'mm/dd/yyyy',
  inputMode: 'numeric' as const,
  maxLength: 10
}

export const DatePicker = (props: DatePickerProps) => {
  const {
    minDate,
    maxDate,
    disabledDays,
    confirmLabel = 'Set date',
    cancelLabel = 'Cancel',
    onCancel,
    onMonthChange,
    className
  } = props
  const isRange = props.mode === 'range'

  const committed = props.value ?? props.defaultValue ?? null

  const [draftSingle, setDraftSingle] = useState<Date | undefined>(
    !isRange && committed ? (committed as Date) : undefined
  )
  const [draftRange, setDraftRange] = useState<DraftRange>(
    isRange && committed ? (committed as DateRange) : {}
  )
  const [month, setMonth] = useState<Date>(() => {
    const anchor = isRange
      ? (committed as DateRange | null)?.from
      : (committed as Date | null)
    return startOfDay(anchor ?? new Date())
  })
  // Mirrors the rendered month, including value re-syncs that call setMonth.
  const monthRef = useRef(month)
  monthRef.current = month
  const changeMonth = (next: Date) => {
    const previous = monthRef.current
    if (
      previous.getFullYear() === next.getFullYear() &&
      previous.getMonth() === next.getMonth()
    )
      return
    monthRef.current = next
    setMonth(next)
    onMonthChange?.(next)
  }

  // Text mirrors of the range draft, so users can type freely
  const [fromText, setFromText] = useState(
    isRange && committed ? formatDate((committed as DateRange).from) : ''
  )
  const [toText, setToText] = useState(
    isRange && committed ? formatDate((committed as DateRange).to) : ''
  )

  // Re-sync drafts when the controlled value changes
  useEffect(() => {
    if (props.value === undefined) return
    if (isRange) {
      const range = props.value as DateRange | null
      setDraftRange(range ?? {})
      setFromText(range ? formatDate(range.from) : '')
      setToText(range ? formatDate(range.to) : '')
      if (range) setMonth(startOfDay(range.from))
    } else {
      const date = props.value as Date | null
      setDraftSingle(date ?? undefined)
      if (date) setMonth(startOfDay(date))
    }
  }, [props.value, isRange])

  const disabledMatchers = useMemo(() => {
    const matchers: Matcher[] = []
    if (minDate) matchers.push({ before: startOfDay(minDate) })
    if (maxDate) matchers.push({ after: startOfDay(maxDate) })
    if (disabledDays) matchers.push(disabledDays)
    return matchers
  }, [minDate, maxDate, disabledDays])

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const first = minDate?.getFullYear() ?? currentYear - 10
    const last = maxDate?.getFullYear() ?? currentYear + 10
    return Array.from({ length: last - first + 1 }, (_, index) => first + index)
  }, [minDate, maxDate])

  const canConfirm = isRange
    ? Boolean(draftRange.from && draftRange.to)
    : Boolean(draftSingle)

  const handleConfirm = () => {
    if (!canConfirm) return
    if (isRange) {
      ;(props as RangeDatePickerProps).onConfirm?.({
        from: draftRange.from as Date,
        to: draftRange.to as Date
      })
    } else {
      ;(props as SingleDatePickerProps).onConfirm?.(draftSingle as Date)
    }
  }

  const handleCancel = () => {
    if (isRange) {
      const range = committed as DateRange | null
      setDraftRange(range ?? {})
      setFromText(range ? formatDate(range.from) : '')
      setToText(range ? formatDate(range.to) : '')
    } else {
      setDraftSingle((committed as Date | null) ?? undefined)
    }
    onCancel?.()
  }

  const handleRangeSelect = (range: DraftRange | undefined) => {
    const next = range ?? {}
    setDraftRange(next)
    setFromText(next.from ? formatDate(next.from) : '')
    setToText(next.to ? formatDate(next.to) : '')
  }

  const handleTypedDate = (edge: 'from' | 'to') => (raw: string) => {
    const previous = edge === 'from' ? fromText : toText
    const text = maskDateText(raw, previous)
    if (edge === 'from') setFromText(text)
    else setToText(text)
    const parsed = parseDate(text)
    if (!parsed) return
    const next = { ...draftRange, [edge]: parsed }
    if (next.from && next.to && next.from > next.to) {
      // Keep the edge being edited; move the other one with it
      if (edge === 'from') next.to = parsed
      else next.from = parsed
      setFromText(formatDate(next.from as Date))
      setToText(formatDate(next.to as Date))
    }
    setDraftRange(next)
    setMonth(startOfDay(parsed))
  }

  const handleTypedBlur = (edge: 'from' | 'to') => () => {
    const draft = draftRange[edge]
    const text = edge === 'from' ? fromText : toText
    if (parseDate(text)) return
    // Revert invalid text to the last valid draft value
    const reverted = draft ? formatDate(draft) : ''
    if (edge === 'from') setFromText(reverted)
    else setToText(reverted)
  }

  const navigateMonth = (offset: number) => {
    const previous = monthRef.current
    changeMonth(
      new Date(previous.getFullYear(), previous.getMonth() + offset, 1)
    )
  }

  return (
    <div
      data-slot='date-picker'
      className={cn(
        'bg-surface border-outline-strong shadow-dialog flex w-fit flex-col gap-4 rounded-lg border px-4 pb-6 pt-4',
        className
      )}
    >
      <div className='flex items-center justify-between gap-2'>
        <button
          type='button'
          aria-label='Previous month'
          onClick={() => navigateMonth(-1)}
          className='text-surface-title-50 hover:bg-surface-200 flex size-6 cursor-pointer items-center justify-center rounded transition-colors'
        >
          <Icon name='keyboard_arrow_left' className='text-[24px]' />
        </button>
        <div className='flex items-center gap-2'>
          <Select
            value={month.getMonth()}
            onValueChange={value => {
              if (value === null) return
              changeMonth(new Date(monthRef.current.getFullYear(), value, 1))
            }}
          >
            <SelectTrigger
              size='sm'
              aria-label='Month'
              className='h-[37px] w-[93px]'
            >
              <SelectValue>{() => MONTHS[month.getMonth()]}</SelectValue>
            </SelectTrigger>
            <SelectContent separators>
              {MONTHS.map((label, index) => (
                <SelectItem key={label} value={index}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={month.getFullYear()}
            onValueChange={value => {
              if (value === null) return
              changeMonth(new Date(value, monthRef.current.getMonth(), 1))
            }}
          >
            <SelectTrigger
              size='sm'
              aria-label='Year'
              className='h-[37px] w-[93px]'
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent separators>
              {years.map(year => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          type='button'
          aria-label='Next month'
          onClick={() => navigateMonth(1)}
          className='text-surface-title-50 hover:bg-surface-200 flex size-6 cursor-pointer items-center justify-center rounded transition-colors'
        >
          <Icon name='keyboard_arrow_right' className='text-[24px]' />
        </button>
      </div>

      {isRange ? (
        <DayPicker
          mode='range'
          showOutsideDays
          month={month}
          onMonthChange={changeMonth}
          selected={
            draftRange.from
              ? { from: draftRange.from, to: draftRange.to }
              : undefined
          }
          onSelect={handleRangeSelect}
          disabled={disabledMatchers}
          classNames={dayPickerClassNames}
        />
      ) : (
        <DayPicker
          mode='single'
          showOutsideDays
          month={month}
          onMonthChange={changeMonth}
          selected={draftSingle}
          onSelect={setDraftSingle}
          disabled={disabledMatchers}
          classNames={{
            ...dayPickerClassNames,
            selected:
              '[&>button]:bg-primary [&>button]:text-on-primary [&>button]:hover:bg-primary-400'
          }}
        />
      )}

      {isRange && (
        <div className='flex items-center gap-2'>
          <label className='flex w-[125px] flex-col gap-1'>
            <span className='text-surface-title-50 text-caption-lg font-semibold'>
              From
            </span>
            <Input
              {...fromToInputProps}
              value={fromText}
              onChange={event => handleTypedDate('from')(event.target.value)}
              onBlur={handleTypedBlur('from')}
            />
          </label>
          <label className='flex w-[125px] flex-col gap-1'>
            <span className='text-surface-title-50 text-caption-lg font-semibold'>
              To
            </span>
            <Input
              {...fromToInputProps}
              value={toText}
              onChange={event => handleTypedDate('to')(event.target.value)}
              onBlur={handleTypedBlur('to')}
            />
          </label>
        </div>
      )}

      <div className='flex flex-col items-center gap-2'>
        <Button
          type='button'
          size='sm'
          variant='primary'
          className='w-full'
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
        <Button
          type='button'
          size='sm'
          variant='secondary'
          className='w-full'
          onClick={handleCancel}
        >
          {cancelLabel}
        </Button>
      </div>
    </div>
  )
}
